import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  NurseMonthlyLimitBulkUpsertRequest,
  NurseMonthlyLimitItem,
  NurseMonthlyLimitListResponse,
} from "@/api/nurses";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";

export interface UseNurseMonthlyLimitsParams {
  year: number;
  month: number;
  groupId?: string | null;
  nurseId?: string | null;
  enabled?: boolean;
}

export interface UseNurseMonthlyLimitsResult {
  data: NurseMonthlyLimitListResponse | undefined;
  currentItem: NurseMonthlyLimitItem | null;
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
}

export function useNurseMonthlyLimits({
  year,
  month,
  groupId,
  nurseId,
  enabled = true,
}: UseNurseMonthlyLimitsParams): UseNurseMonthlyLimitsResult {
  const query = useQuery<NurseMonthlyLimitListResponse, Error>({
    queryKey: QUERY_KEYS.nurses.monthlyLimits(groupId, nurseId),
    queryFn: () =>
      httpClient.get<NurseMonthlyLimitListResponse>(
        "/nurses/monthly-limits",
        {
          params: {
            ...(groupId && { group_id: groupId }),
            ...(nurseId && { nurse_id: nurseId }),
          },
        }
      ),
    enabled: enabled && !!groupId && !!nurseId,
    staleTime: 5 * 60 * 1000,
  });

  const currentItem =
    nurseId && groupId
      ? (query.data?.items.find(
          (item) =>
            item.nurse_id === nurseId &&
            item.group_id === groupId &&
            item.year === year &&
            item.month === month
        ) ?? null)
      : null;

  return {
    data: query.data,
    currentItem,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
  };
}

export interface UseSaveNurseMonthlyLimitsParams {
  year: number;
  month: number;
  groupId?: string | null;
  nurseId?: string | null;
  onSuccess?: (response: NurseMonthlyLimitListResponse) => void;
  showToast?: boolean;
}

function getMonthlyLimitScopeKey(item: NurseMonthlyLimitItem) {
  return `${item.nurse_id}:${item.group_id}:${item.year}:${item.month}`;
}

function mergeMonthlyLimitResponse(
  previous: NurseMonthlyLimitListResponse | undefined,
  response: NurseMonthlyLimitListResponse,
  request: NurseMonthlyLimitBulkUpsertRequest
): NurseMonthlyLimitListResponse {
  if (!previous) return response;

  const responseItemMap = new Map(
    [...request.limits, ...response.items].map((item) => [
      getMonthlyLimitScopeKey(item),
      item,
    ])
  );
  const previousItemKeys = new Set(
    previous.items.map((item) => getMonthlyLimitScopeKey(item))
  );

  const mergedItems = previous.items.map((item) => {
    return responseItemMap.get(getMonthlyLimitScopeKey(item)) ?? item;
  });

  response.items.forEach((item) => {
    const key = getMonthlyLimitScopeKey(item);
    if (!previousItemKeys.has(key)) {
      mergedItems.push(item);
    }
  });

  return {
    ...previous,
    ...response,
    items: mergedItems,
  };
}

export function useSaveNurseMonthlyLimits({
  year,
  month,
  groupId,
  nurseId,
  onSuccess,
  showToast = true,
}: UseSaveNurseMonthlyLimitsParams) {
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.nurses.monthlyLimits(groupId, nurseId);

  return useMutation<
    NurseMonthlyLimitListResponse,
    Error,
    NurseMonthlyLimitBulkUpsertRequest
  >({
    mutationFn: (payload) =>
      httpClient.put<NurseMonthlyLimitListResponse>(
        "/nurses/monthly-limits",
        payload
      ),
    onSuccess: (response, payload) => {
      queryClient.setQueryData<NurseMonthlyLimitListResponse | undefined>(
        queryKey,
        (previous) => mergeMonthlyLimitResponse(previous, response, payload)
      );
      if (showToast) {
        toast.success("근무형태 개수 설정이 저장되었습니다.");
        response.warnings?.forEach((warning) => {
          toast.info(warning.message);
        });
      }
      onSuccess?.(response);
    },
  });
}
